// JQ4-37 · US-PAGOS-003 — TipConfigPage
//
// Admin page to configure per-location tip settings.
// GET /admin/locations/:locationId/tip-config (mirrors CheckinConfigPage pattern)
//
// Controls:
//   - tipsEnabled toggle (enable/disable tips for this location)
//   - suggestedPercentages: up to 6 preset % buttons (15, 18, 20, 22, 25, 30)
//   - customAllowed: allow visitor to enter an arbitrary tip amount

import { ApiError, getLocationTipConfig, updateLocationTipConfig } from '@jq4race/api-client';
import { useTranslation } from '@jq4race/i18n';
import type { TipConfigResponse } from '@jq4race/shared';
import { Button, Card, CardContent } from '@jq4race/ui';
import { useParams } from '@tanstack/react-router';
import { CheckCircle2, TriangleAlert, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { translateApiError } from '../../../../shared/errors/translateApiError';
import { formatDateTime } from '../../../../shared/format/format-date';
import { PageHeader } from '../../../app-shell/ui/PageHeader';

// ── Constants ─────────────────────────────────────────────────────────────────

const PRESET_PERCENTAGES = [15, 18, 20, 22, 25, 30];

// ── Component ─────────────────────────────────────────────────────────────────

export function TipConfigPage() {
  const { t, i18n } = useTranslation('location-tip-config');
  const { locationId } = useParams({ strict: false });

  const [config, setConfig] = useState<TipConfigResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [tipsEnabled, setTipsEnabled] = useState(false);
  const [selectedPcts, setSelectedPcts] = useState<number[]>([]);
  const [customAllowed, setCustomAllowed] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [errorBanner, setErrorBanner] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!locationId) return;
    setLoading(true);
    try {
      const response = await getLocationTipConfig(locationId);
      setConfig(response);
      setTipsEnabled(response.tipsEnabled);
      setSelectedPcts(response.suggestedPercentages);
      setCustomAllowed(response.customAllowed);
    } catch (e) {
      setErrorBanner(translateApiError(e, t, { fallback: t('feedback.loadError') }));
    } finally {
      setLoading(false);
    }
  }, [locationId, t]);

  useEffect(() => {
    void load();
  }, [load]);

  // Auto-dismiss success banner
  useEffect(() => {
    if (!success) return;
    const id = window.setTimeout(() => setSuccess(null), 5000);
    return () => window.clearTimeout(id);
  }, [success]);

  function togglePercentage(pct: number) {
    setSelectedPcts((prev) =>
      prev.includes(pct) ? prev.filter((p) => p !== pct) : [...prev, pct].sort((a, b) => a - b),
    );
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!locationId) return;
    setSubmitting(true);
    setErrorBanner(null);
    try {
      const saved = await updateLocationTipConfig(locationId, {
        tipsEnabled,
        suggestedPercentages: selectedPcts,
        customAllowed,
      });
      setConfig(saved);
      setTipsEnabled(saved.tipsEnabled);
      setSelectedPcts(saved.suggestedPercentages);
      setCustomAllowed(saved.customAllowed);
      setSuccess(t('feedback.saveSuccess'));
    } catch (e) {
      if (e instanceof ApiError) {
        setErrorBanner(translateApiError(e, t, { fallback: t('feedback.saveError') }));
      } else {
        setErrorBanner(translateApiError(e, t, { fallback: t('feedback.unexpectedError') }));
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="space-y-8">
      <PageHeader
        eyebrow={t('page.eyebrow')}
        title={t('page.title')}
        description={t('page.description')}
      />

      {errorBanner && (
        <FeedbackBanner tone="error" message={errorBanner} onDismiss={() => setErrorBanner(null)} />
      )}
      {success && (
        <FeedbackBanner tone="success" message={success} onDismiss={() => setSuccess(null)} />
      )}

      <Card className="border-jq-sand/70 bg-jq-surface-0 shadow-sm">
        <CardContent className="p-6 sm:p-8">
          {loading ? (
            <p className="text-[13px] text-jq-graphite">{t('loading')}</p>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Enable tips toggle */}
              <fieldset className="space-y-3">
                <legend className="text-[13px] font-medium text-jq-graphite">
                  {t('form.tipsLegend')}
                </legend>
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="tips-enabled"
                    checked={tipsEnabled}
                    onChange={(e) => setTipsEnabled(e.target.checked)}
                    className="h-4 w-4 rounded accent-jq-primary"
                  />
                  <label htmlFor="tips-enabled" className="text-[13px] text-jq-graphite">
                    {t('form.enableTips')}
                  </label>
                </div>
              </fieldset>

              {/* Suggested percentages (only shown when tips enabled) */}
              {tipsEnabled && (
                <>
                  <fieldset className="space-y-3">
                    <legend className="text-[13px] font-medium text-jq-graphite">
                      {t('form.percentagesLegend')}
                      <span className="ml-1 font-normal text-jq-graphite/60">
                        {t('form.percentagesCount', { count: PRESET_PERCENTAGES.length })}
                      </span>
                    </legend>
                    <p className="text-[12px] text-jq-graphite/60">{t('form.percentagesHint')}</p>
                    <div className="flex flex-wrap gap-2">
                      {PRESET_PERCENTAGES.map((pct) => (
                        <label
                          key={pct}
                          htmlFor={`tip-pct-${pct}`}
                          className="flex cursor-pointer items-center gap-2 rounded-md border border-jq-sand/70 px-3 py-1.5 text-[13px] text-jq-graphite hover:bg-jq-sand/20 has-[:checked]:border-jq-primary has-[:checked]:bg-jq-primary/10"
                        >
                          <input
                            type="checkbox"
                            id={`tip-pct-${pct}`}
                            value={pct}
                            aria-label={t('form.percentageAria', { pct })}
                            checked={selectedPcts.includes(pct)}
                            onChange={() => togglePercentage(pct)}
                            className="h-3.5 w-3.5 accent-jq-primary"
                          />
                          {t('form.percentageLabel', { pct })}
                        </label>
                      ))}
                    </div>
                  </fieldset>

                  {/* Custom tip toggle */}
                  <fieldset className="space-y-3">
                    <legend className="text-[13px] font-medium text-jq-graphite">
                      {t('form.customTipLegend')}
                    </legend>
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        id="custom-allowed"
                        checked={customAllowed}
                        onChange={(e) => setCustomAllowed(e.target.checked)}
                        className="h-4 w-4 rounded accent-jq-primary"
                      />
                      <label htmlFor="custom-allowed" className="text-[13px] text-jq-graphite">
                        {t('form.customAllowed')}
                      </label>
                    </div>
                  </fieldset>
                </>
              )}

              {/* Current saved state (info only) */}
              {config && (
                <p className="text-[12px] text-jq-graphite/60">
                  {config.updatedAt
                    ? t('form.lastSaved', { date: formatDateTime(config.updatedAt, i18n.language) })
                    : t('form.noConfigSaved')}
                </p>
              )}

              <Button type="submit" disabled={submitting}>
                {submitting ? t('form.saving') : t('form.save')}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ── Feedback banner ────────────────────────────────────────────────────────────

interface FeedbackBannerProps {
  tone: 'error' | 'success';
  message: string | null;
  onDismiss: () => void;
}

function FeedbackBanner({ tone, message, onDismiss }: FeedbackBannerProps) {
  const { t } = useTranslation('location-tip-config');
  if (!message) return null;
  const Icon = tone === 'success' ? CheckCircle2 : TriangleAlert;
  const palette =
    tone === 'success'
      ? 'border-jq-success/40 bg-jq-success/10 text-jq-success'
      : 'border-jq-danger/40 bg-jq-danger/10 text-jq-danger';
  return (
    <div
      role={tone === 'error' ? 'alert' : 'status'}
      className={`flex items-start gap-3 rounded-sm border px-4 py-3 text-[13px] ${palette}`}
    >
      <Icon className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={1.75} />
      <p className="flex-1 leading-snug">{message}</p>
      <button
        type="button"
        onClick={onDismiss}
        aria-label={t('feedback.dismiss')}
        className="-mr-1 -mt-1 rounded p-1 opacity-70 transition hover:opacity-100"
      >
        <X className="h-4 w-4" strokeWidth={1.75} />
      </button>
    </div>
  );
}
